import { html, nothing, type TemplateResult } from "lit";
import {
  estimatedTotals,
  formatCents,
  type OrderTotals,
} from "../../checkout/order-totals";

export interface CheckoutTotalsViewModel {
  /** Cart arithmetic in dollars, used until a server quote arrives. */
  subtotal: number;
  discount: number;
  /** The server's quote for this cart, when one has been fetched. */
  quote?: OrderTotals | undefined;
  /** Org ISO-4217 currency; USD when unset. */
  currency?: string | undefined;
}

/**
 * The order-summary money block, shared by the booking and purchase checkouts.
 *
 * Both used to print a literal `Tax $0.00` and no fee line, while the charge
 * they were summarising added Stripe Tax and — for an org with
 * `passFeesToCustomer` — a platform fee. So this renders the server's quote
 * when there is one, and otherwise says the tax and fees are still to be
 * calculated, which is what POS's pre-finalization cart says too. It never
 * invents either number: they depend on the buyer's resolved tax location and
 * the org's payment tier, neither of which the browser knows.
 */
export function renderCheckoutTotals(
  model: CheckoutTotalsViewModel,
): TemplateResult {
  const t = model.quote ?? estimatedTotals(model.subtotal, model.discount);
  return html`
    <div class="totals">
      <div class="li">
        <span>Subtotal</span
        ><span>${formatCents(t.subtotalInCents, model.currency)}</span>
      </div>
      ${t.discountInCents
        ? html`<div class="li">
            <span>Discount</span
            ><span>−${formatCents(t.discountInCents, model.currency)}</span>
          </div>`
        : nothing}
      ${t.quoted && t.platformFeeInCents
        ? html`<div class="li">
            <span>Processing fee</span
            ><span>${formatCents(t.platformFeeInCents, model.currency)}</span>
          </div>`
        : nothing}
      ${t.quoted && t.taxInCents
        ? html`<div class="li">
            <span>Tax</span
            ><span>${formatCents(t.taxInCents, model.currency)}</span>
          </div>`
        : nothing}
      <div class="total">
        <span>Total</span
        ><span>${formatCents(t.totalInCents, model.currency)}</span>
      </div>
      ${t.quoted
        ? nothing
        : html`<p class="totals-note">
            Taxes and fees calculated at checkout
          </p>`}
    </div>
  `;
}
